import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSpaces } from '../context/SpaceContext';
import { uploadFile, getSpaceFiles, getDirectFiles, downloadFile, openFile, deleteFile } from '../services/fileService';
import { getMyFiles } from '../services/meService';
import {
  getFileAuditTrail,
  listExternalShareGrants,
  createExternalShareGrant,
  revokeExternalShareGrant,
} from '../services/fileGovernanceService';
import './FilesPage.css';

const ALL_ACCESSIBLE_SCOPE = '__all_accessible__';
const DIRECT_SCOPE = '__direct__';

export const getVisibleFileActions = (file = {}) => {
  const actions = file.availableActions || {};
  return {
    open: actions.open !== false,
    share: Boolean(actions.share),
    download: Boolean(actions.download),
    history: Boolean(actions.history || actions.access),
    access: Boolean(actions.access),
    delete: Boolean(actions.delete),
    more: Boolean(actions.download || actions.history || actions.access || actions.delete),
  };
};

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fileGlyph(mimeType = '') {
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('officedocument.wordprocessingml')) return 'DOC';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PPT';
  if (mimeType.startsWith('image/')) return 'IMG';
  if (mimeType.startsWith('video/')) return 'VID';
  if (mimeType.startsWith('audio/')) return 'AUD';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'ZIP';
  return 'FILE';
}

export default function FilesPage() {
  const { spaces } = useSpaces();
  const [selectedSpaceId, setSelectedSpaceId] = useState(ALL_ACCESSIBLE_SCOPE);
  const [files, setFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [expandedActionsFileId, setExpandedActionsFileId] = useState('');

  // Advanced file details modals
  const [activeAuditFile, setActiveAuditFile] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [activeShareFile, setActiveShareFile] = useState(null);
  const [shareGrants, setShareGrants] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [newShareEmail, setNewShareEmail] = useState('');
  const [newShareExpiryDays, setNewShareExpiryDays] = useState(3);
  const [newShareDownload, setNewShareDownload] = useState(false);
  const [shareCreating, setShareCreating] = useState(false);
  const [shareError, setShareError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');

  const loadFiles = useCallback(() => {
    setLoading(true);
    setError('');

    let loader;
    if (selectedSpaceId === ALL_ACCESSIBLE_SCOPE) {
      loader = getMyFiles().then((res) => ({ data: { files: res.data.files || [] } }));
    } else if (selectedSpaceId === DIRECT_SCOPE) {
      loader = getDirectFiles();
    } else {
      loader = getSpaceFiles(selectedSpaceId);
    }

    loader
      .then((res) => setFiles(res.data.files || []))
      .catch(() => setError('Failed to load files.'))
      .finally(() => setLoading(false));
  }, [selectedSpaceId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    const targetSpace = selectedSpaceId === ALL_ACCESSIBLE_SCOPE || selectedSpaceId === DIRECT_SCOPE ? null : selectedSpaceId;
    setUploading(true);
    setError('');
    try {
      for (const file of fileList) {
        const res = await uploadFile(targetSpace, file);
        setFiles((prev) => [res.data.file, ...prev]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(Array.from(e.dataTransfer.files));
  };

  const handleDownload = async (file) => {
    try {
      await downloadFile(file._id, file.originalName || file.name || 'document');
    } catch (err) {
      setError(err?.response?.data?.message || 'Download failed.');
    }
  };

  const handleOpen = async (file) => {
    try {
      await openFile(file._id);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to open file.');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteFile(fileId);
      setFiles((prev) => prev.filter((f) => f._id !== fileId));
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed.');
    }
  };

  // Open History Modal
  const openAuditTrail = async (file) => {
    setActiveAuditFile(file);
    setAuditLoading(true);
    try {
      const res = await getFileAuditTrail(file._id);
      setAuditTrail(res.data.auditTrail || []);
    } catch (err) {
      console.error('Failed to load audit trail:', err);
      setAuditTrail([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // Open Share Modal
  const openShareManager = async (file) => {
    setActiveShareFile(file);
    setShareError('');
    setShareLoading(true);
    try {
      const res = await listExternalShareGrants(file._id);
      setShareGrants(res.data.grants || []);
    } catch (err) {
      console.error('Failed to load share grants:', err);
      setShareGrants([]);
    } finally {
      setShareLoading(false);
    }
  };

  const handleCreateShareGrant = async (e) => {
    e.preventDefault();
    if (!newShareEmail.trim() || !activeShareFile) return;
    setShareCreating(true);
    setShareError('');
    try {
      const expiresAt = new Date(Date.now() + newShareExpiryDays * 86400000).toISOString();
      const res = await createExternalShareGrant(activeShareFile._id, {
        recipientAddress: newShareEmail.trim(),
        expiresAt,
        downloadAllowed: newShareDownload,
        permissions: newShareDownload ? ['view', 'download'] : ['view'],
      });
      setShareGrants((prev) => [res.data.grant, ...prev]);
      setNewShareEmail('');
    } catch (err) {
      setShareError(err?.response?.data?.message || 'Failed to create share grant.');
    } finally {
      setShareCreating(false);
    }
  };

  const handleRevokeShareGrant = async (grantId) => {
    if (!window.confirm('Revoke this external share link immediately?')) return;
    try {
      await revokeExternalShareGrant(activeShareFile._id, grantId);
      setShareGrants((prev) =>
        prev.map((g) => (g._id === grantId ? { ...g, revokedAt: new Date().toISOString() } : g))
      );
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to revoke grant.');
    }
  };

  const handleCopyLink = (token) => {
    const fullUrl = `${window.location.origin}/api/external-shares/${token}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 2000);
    });
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredFiles = files.filter((file) => {
    if (!normalizedSearch) return true;
    const haystack = [
      file.originalName,
      file.name,
      file.mimeType,
      file.uploadedBy?.name,
      file.uploadedBy?.email,
      file.spaceId?.name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedSearch);
  });

  return (
    <div className="files-page">
      <div className="files-header">
        <div>
          <h1>Files</h1>
          <p className="dashboard-sub">Find, open and share the files you can access.</p>
        </div>
        <div className="files-controls">
          <select
            className="space-select"
            value={selectedSpaceId}
            onChange={(e) => setSelectedSpaceId(e.target.value)}
          >
            <option value={ALL_ACCESSIBLE_SCOPE}>All My Files</option>
            <option value={DIRECT_SCOPE}>Personal Files</option>
            {spaces.map((s) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
          <input
            type="text"
            className="files-search-input"
            placeholder="Search by name, space, uploader..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className="btn btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : '+ Upload File'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleUpload(Array.from(e.target.files))}
          />
        </div>
      </div>

      {error && <div className="files-error">{error}</div>}

      <div
        className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <span>Drag and drop files here to upload.</span>
      </div>

      {loading ? (
        <p className="files-muted">Loading files...</p>
      ) : filteredFiles.length === 0 ? (
        <p className="files-muted">
          {normalizedSearch
            ? 'No files match your search filter.'
            : 'No files available in this view.'}
        </p>
      ) : (
        <table className="files-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Location</th>
              <th>Size</th>
              <th>Uploaded By</th>
              <th>Date</th>
              <th className="actions-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.map((f) => {
              const visibleActions = getVisibleFileActions(f);
              return (
              <tr key={f._id}>
                <td className="file-name">
                  <div className="file-title-wrap">
                    <span className="file-glyph">{fileGlyph(f.mimeType)}</span>
                    <div className="file-title-meta">
                      <button
                        type="button"
                        className="file-title file-open-link"
                        onClick={() => handleOpen(f)}
                        title="View file"
                      >
                        {f.originalName || f.name}
                      </button>
                      <span className="file-title-sub">{f.mimeType || 'Unknown type'}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="file-context-tag">
                    {f.spaceId?.name || f.custody?.ownerType || 'Workspace'}
                  </span>
                  {f.custody?.classification && (
                    <span className={`file-class-tag class-${f.custody.classification}`}>
                      {f.custody.classification}
                    </span>
                  )}
                </td>
                <td>{formatSize(f.size)}</td>
                <td>{f.uploadedBy?.name || '—'}</td>
                <td>{formatDate(f.createdAt)}</td>
                <td className="file-actions-cell">
                  {visibleActions.open && <button
                    className="btn btn-xs btn-primary"
                    onClick={() => handleOpen(f)}
                    title="Open file"
                  >
                    Open
                  </button>}
                  {visibleActions.share && <button
                    className="btn btn-xs btn-share-grant"
                    onClick={() => openShareManager(f)}
                    title="Share file"
                  >
                    Share
                  </button>}
                  {visibleActions.more && <button
                    className="btn btn-xs btn-ghost"
                    onClick={() => setExpandedActionsFileId((current) => current === f._id ? '' : f._id)}
                    title="More actions"
                  >
                    ...
                  </button>}
                  {expandedActionsFileId === f._id && (
                    <div className="file-more-actions">
                      {visibleActions.download && <button type="button" className="btn btn-xs btn-ghost" onClick={() => handleDownload(f)}>Download</button>}
                      {visibleActions.history && <button type="button" className="btn btn-xs btn-audit" onClick={() => openAuditTrail(f)}>History</button>}
                      {visibleActions.delete && <button type="button" className="btn-icon btn-danger" onClick={() => handleDelete(f._id)} title="Delete file">✕</button>}
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── History Modal ── */}
      {activeAuditFile && (
        <div className="modal-backdrop" onClick={() => setActiveAuditFile(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>History: {activeAuditFile.originalName || activeAuditFile.name}</h3>
              <button className="btn-icon" onClick={() => setActiveAuditFile(null)}>✕</button>
            </div>
            <div className="modal-body">
              {auditLoading ? (
                <p className="muted">Loading history...</p>
              ) : auditTrail.length === 0 ? (
                <p className="muted">No recorded history for this file.</p>
              ) : (
                <ul className="audit-event-list">
                  {auditTrail.map((entry, idx) => (
                    <li key={idx} className="audit-event-item">
                      <div className="audit-event-top">
                        <span className={`audit-action-tag action-${entry.action}`}>{entry.action}</span>
                        <span className="audit-date">{formatDateTime(entry.occurredAt)}</span>
                      </div>
                      <div className="audit-event-detail">
                        <span className="audit-actor">By: {entry.actor?.name || 'Authorized user'}</span>
                        {entry.note && <span className="audit-note">{entry.note}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setActiveAuditFile(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share Modal ── */}
      {activeShareFile && (
        <div className="modal-backdrop" onClick={() => setActiveShareFile(null)}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Share</h3>
                <p className="modal-sub">
                  Send an expiring access link for this file.
                </p>
              </div>
              <button className="btn-icon" onClick={() => setActiveShareFile(null)}>✕</button>
            </div>

            <div className="modal-body">
              {shareError && <div className="files-error">{shareError}</div>}

              {/* Create Share Form */}
              <form className="create-grant-box" onSubmit={handleCreateShareGrant}>
                <h4>New Share Link</h4>
                <div className="form-row">
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Recipient email address *"
                    value={newShareEmail}
                    onChange={(e) => setNewShareEmail(e.target.value)}
                    required
                  />
                  <select
                    className="form-input form-input-sm"
                    value={newShareExpiryDays}
                    onChange={(e) => setNewShareExpiryDays(Number(e.target.value))}
                  >
                    <option value={1}>Expires in 24 Hours</option>
                    <option value={3}>Expires in 3 Days</option>
                    <option value={7}>Expires in 7 Days</option>
                    <option value={30}>Expires in 30 Days</option>
                  </select>
                  <label className="form-check">
                    <input
                      type="checkbox"
                      checked={newShareDownload}
                      onChange={(e) => setNewShareDownload(e.target.checked)}
                    />
                    Allow Download
                  </label>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={shareCreating}>
                    {shareCreating ? 'Creating...' : 'Create Link'}
                  </button>
                </div>
              </form>

              {/* Active & Historical Links */}
              <div className="grants-list-wrap">
                <h4>Shared Links</h4>
                {shareLoading ? (
                  <p className="muted">Loading links...</p>
                ) : shareGrants.length === 0 ? (
                  <p className="muted">No share links created for this file.</p>
                ) : (
                  <ul className="grants-list">
                    {shareGrants.map((g) => {
                      const isExpired = new Date(g.expiresAt) < new Date();
                      const isRevoked = Boolean(g.revokedAt);
                      const isValid = !isExpired && !isRevoked;

                      return (
                        <li key={g._id} className={`grant-item ${isValid ? 'grant-valid' : 'grant-inactive'}`}>
                          <div className="grant-info">
                            <div className="grant-recipient-row">
                              <strong>{g.recipientAddress}</strong>
                              <span className={`grant-status-pill ${isValid ? 'status-active' : 'status-revoked'}`}>
                                {isRevoked ? 'Revoked' : isExpired ? 'Expired' : 'Active'}
                              </span>
                              <span className="grant-perm-pill">
                                {g.downloadAllowed ? 'View & Download' : 'View Only'}
                              </span>
                            </div>
                            <div className="grant-meta-row">
                              <span>Expires: {formatDateTime(g.expiresAt)}</span>
                              <span>Accesses: {g.accessCount || 0}</span>
                              {g.lastAccessedAt && <span>Last accessed: {formatDateTime(g.lastAccessedAt)}</span>}
                            </div>
                          </div>

                          <div className="grant-actions">
                            {isValid && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-ghost"
                                  onClick={() => handleCopyLink(g.token)}
                                >
                                  {copiedToken === g.token ? '✓ Copied Link' : 'Copy Link'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-danger"
                                  onClick={() => handleRevokeShareGrant(g._id)}
                                >
                                  Remove Access
                                </button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setActiveShareFile(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
